/**
 * The public grading entry point (SPEC §2 `lang/validator.ts`, §1 step 4).
 *
 * **Pure. Offline. No network, ever.** This is the runtime half of the correctness design: the
 * build-time LLM pass (T42) vets the corpus, and this decides whether what the learner typed is
 * right, with no API call between them.
 *
 * Two rules from the brief shape the whole return value:
 *
 *  1. *"Accent-insensitive and case-insensitive matching for **scoring**, but always show the
 *     correctly accented form in feedback and flag the missing accent explicitly."* So an
 *     accent-only miss is **correct**, and carries `nearMiss: 'missing-accent'` — which
 *     `gate.ratingFor` turns into `hard` rather than `again`, costing interval without resetting
 *     the item.
 *  2. Feedback *names* the error. `message` is never "wrong".
 */

import { matchesExactly, matchesIgnoringAccents } from './accents';
import { NEAR_MISS_LABELS, classify, type ClassifyContext, type NearMissKind } from './nearmiss';

export interface GradeRequest {
  /** The correct answer, spelled correctly, accents and all. From the vetted corpus. */
  expected: string;
  /** What the learner typed or said. */
  actual: string;
  context?: ClassifyContext;
}

export interface Grade {
  /** Whether this counts as correct. Accent-only misses are correct (rule 1 above). */
  correct: boolean;
  nearMiss: NearMissKind | null;
  /** ALWAYS the correctly accented expected form. The learner sees this every time. */
  correctForm: string;
  /** Names what happened. Never a bare "wrong". */
  message: string;
  /** An accent error was flagged explicitly, as the brief requires. */
  accentFlagged: boolean;
}

/**
 * Grade one answer.
 *
 * `correctForm` is returned on success as well as failure, on purpose: seeing the right spelling
 * after a correct answer is free reinforcement, and it means the feedback card never has to decide
 * whether to show it.
 */
export function grade(request: GradeRequest): Grade {
  const expected = request.expected.trim();
  const actual = request.actual.trim();
  const nearMiss = classify(expected, actual, request.context ?? {});

  const accentOnly = nearMiss === 'missing-accent' || nearMiss === 'wrong-accent';
  const correct = nearMiss === null || accentOnly;

  return {
    correct,
    nearMiss,
    correctForm: expected,
    message: messageFor(nearMiss, expected, actual),
    accentFlagged: accentOnly,
  };
}

function messageFor(kind: NearMissKind | null, expected: string, actual: string): string {
  if (kind === null) return `Correct — ${expected}.`;
  const label = NEAR_MISS_LABELS[kind];
  if (kind === 'unrelated') return `${label} The answer is ${expected}.`;
  if (kind === 'missing-accent' || kind === 'wrong-accent') {
    // Counted as correct, and the accent is still named — that is the point of flagging it.
    return `${label} ${expected}, not ${actual}.`;
  }
  return `${label} ${expected}, not ${actual}.`;
}

/**
 * Whether an answer would score as correct, without building the feedback.
 *
 * Exactly `grade().correct`, and kept as a separate name so the round loop's hot path does not
 * read as though it is throwing feedback away.
 */
export function isCorrect(expected: string, actual: string, context: ClassifyContext = {}): boolean {
  return grade({ expected, actual, context }).correct;
}

/**
 * Did the learner spell it exactly right, accents included?
 *
 * Separate from `isCorrect` because they answer different questions and conflating them is how an
 * accent-insensitive scorer quietly becomes an accent-blind one.
 */
export function isExact(expected: string, actual: string): boolean {
  return matchesExactly(expected.trim(), actual.trim());
}

/** Right letters, whatever the accents. The scoring comparison, exposed for tests and telemetry. */
export function matchesForScoring(expected: string, actual: string): boolean {
  return matchesIgnoringAccents(expected.trim(), actual.trim());
}
